import React, { useEffect, useCallback, useState } from 'react';
import { Grid, Typography, TextField, Button } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import AddIcon from '@material-ui/icons/Add';
import { LineMaterialSchema } from '../../schemas';
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import btnStyles from '../../styles';
import { v4 as uuidv4 } from 'uuid';
import { useMaterialLabelsDnd } from '../hooks/useMaterialLabelsDnd';
import { GridDND, SortableItem, Item } from '../materialDndComponents/materialDndComponents';

const LineSpecifications = ({
  values,
  remove,
  push,
  replace,
  move,
  type,
  classes,
  data,
  setIsCorrectRequest,
}) => {
  const [editIndex, setEditIndex] = useState(null);
  const [id, setId] = useState('');
  const [width, setWidth] = useState('');
  const [ratio, setRatio] = useState('');
  const [testQuantity, setTestQuantity] = useState('');
  const [lineResult, setLineResult] = useState('');
  const [homepageStats, setHomepageStats] = useState('');
  const [errors, setErrors] = useState({});

  const { items, setItems, activeId, sensors, handleDragStart, handleDragEnd, handleDragCancel } =
    useMaterialLabelsDnd({ values, move });

  const handleChangeHomepageStats = (e) => {
    if (e.target.value === homepageStats) {
      setHomepageStats('');
    } else {
      setHomepageStats(e.target.value);
    }
  };

  const clearErrors = useCallback(
    (prop) => {
      if (!prop) {
        setErrors({});
      } else {
        const errorsObject = Object.assign({}, errors);
        delete errorsObject[prop];
        setErrors(errorsObject);
      }
    },
    [errors]
  );

  const resetForm = () => {
    setWidth('');
    setRatio('');
    setTestQuantity('');
    setLineResult('');
    setHomepageStats('');
    setId('');
    clearErrors();
  };

  const addLine = useCallback(() => {
    try {
      const result = {
        width,
        ratio,
        homepageStats,
        testQuantity,
        _id: uuidv4(),
      };

      const validated = LineMaterialSchema(values || []).validateSync(result, {
        abortEarly: false,
        stripUnknown: true,
      });

      push(validated);
      setItems((dragItems) => [...dragItems, validated]);
      setIsCorrectRequest(true);
      resetForm();
    } catch (err) {
      const errObject = {};
      (err.inner || []).forEach(({ path, message }) => (errObject[path] = message));
      setErrors(errObject);
    }
  }, [push, width, ratio, homepageStats, testQuantity, values, id]);

  const editSave = () => {
    try {
      const result = {
        width,
        ratio,
        homepageStats,
        _id: id,
      };

      const validated = LineMaterialSchema(values || [], editIndex).validateSync(result, {
        abortEarly: false,
        stripUnknown: true,
      });

      replace(editIndex, validated);
      setItems((dragItems) => dragItems.map((el) => (el._id === validated._id ? validated : el)));

      setIsCorrectRequest(true);
      resetForm();
      setEditIndex(null);
    } catch (err) {
      const errObject = {};
      (err.inner || []).forEach(({ path, message }) => (errObject[path] = message));
      setErrors(errObject);
    }
  };

  useEffect(() => {
    if (Number(testQuantity) > 0 && Number(ratio) > 0) {
      setLineResult((Number(testQuantity) / Number(ratio)).toFixed(2));
    } else {
      setLineResult('');
    }
  }, [ratio, testQuantity]);

  return (
    <Grid container xs={12} style={{ borderTop: '1px solid #D9DAE3', marginBottom: '15px' }}>
      <Grid item xs={8} md={'auto'} style={{ marginBottom: '17px', marginTop: '10px' }}>
        <Typography variant="h5">Line Specifications:</Typography>
      </Grid>

      <Grid container spacing={1} style={{ justifyContent: 'space-between' }}>
        <Grid item xs={6} lg={3}>
          <label htmlFor="lineWidth">
            <Typography variant="body1" color="textSecondary">
              {'Available Width (in)'}
            </Typography>
            <TextField
              id="lineWidth"
              variant="outlined"
              value={width}
              type="number"
              className="materials-input"
              InputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                className: `${errors.width ? 'materials-input--error' : ''}`,
              }}
              fullWidth={true}
              onChange={(e) => {
                setWidth(e.target.value);
                clearErrors('width');
              }}
            />
          </label>
          {errors.width && <span className="materials-input--error-text">{errors.width}</span>}
        </Grid>
        <Grid item xs={6} lg={3}>
          <label htmlFor="lineRatio">
            <Typography variant="body1" color="textSecondary">
              {type && type === 'volume' && 'Ratio LF/Gal'}
              {type && type === 'weight' && 'Ratio LF/Lbs'}
            </Typography>
            <TextField
              id="lineRatio"
              value={ratio}
              type="number"
              variant="outlined"
              className="materials-input"
              InputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                className: `${errors.ratio ? 'materials-input--error' : ''}`,
              }}
              fullWidth={true}
              onChange={(e) => {
                setRatio(e.target.value);
                clearErrors('ratio');
              }}
            />
          </label>
          {errors.ratio && <span className="materials-input--error-text">{errors.ratio}</span>}
        </Grid>
        <Grid item xs={6} lg={3}>
          <label htmlFor="lineTestQuantity">
            <Typography variant="body1" color="textSecondary">
              Test Value LF
            </Typography>
            <TextField
              id="lineTestQuantity"
              value={testQuantity}
              variant="outlined"
              className="materials-input"
              type="number"
              InputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                className: `${errors.testQuantity ? 'materials-input--error' : ''}`,
              }}
              fullWidth={true}
              onChange={(e) => setTestQuantity(e.target.value)}
            />
          </label>
          {errors.testQuantity && (
            <span className="materials-input--error-text">{errors.testQuantity}</span>
          )}
        </Grid>
        <Grid item xs={6} lg={3}>
          <label htmlFor="lineResult">
            <Typography variant="body1" color="textSecondary">
              {((type && type === 'volume') || (data && data.type === 'volume')) && 'Result LF/Gal'}
              {((type && type === 'weight') || (data && data.type === 'weight')) && 'Result LF/Lbs'}
            </Typography>
            <TextField
              id="lineResult"
              value={lineResult}
              variant="outlined"
              InputProps={{
                type: 'number',
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              fullWidth={true}
              disabled={true}
            />
          </label>
        </Grid>
      </Grid>
      <Grid container spacing={1} style={{ justifyContent: 'flex-end' }}>
        {!Number.isInteger(editIndex) && (
          <Grid item xs={'auto'} style={{}}>
            <Button
              type="button"
              variant="outlined"
              size="medium"
              color="primary"
              style={{ padding: '2px 7px 2px 3px', marginTop: '15px' }}
              onClick={addLine}
              className={classes.addButton}
            >
              <AddIcon fontSize="small" color="primary" />
              <span>Add</span>
            </Button>
          </Grid>
        )}
        {!!Number.isInteger(editIndex) && (
          <>
            <Grid item xs={'auto'} style={{}}>
              <Button
                variant="outlined"
                size="medium"
                style={{ padding: '2px', marginTop: '15px' }}
                onClick={(e) => {
                  setEditIndex(null);
                  resetForm();
                }}
                className={classes.cancelEddingButton}
              >
                <span>Cancel</span>
              </Button>
            </Grid>
            <Grid item xs={'auto'} style={{}}>
              <Button
                variant="outlined"
                size="medium"
                color="primary"
                style={{ padding: '2px 10px', marginTop: '15px' }}
                onClick={editSave}
                className={classes.addButton}
              >
                <span>Save</span>
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      <Grid id="radiocb">
        <Typography variant="body1" color="textSecondary">
          Homepage Statistics
        </Typography>
        <Grid style={{ display: 'flex', alignItems: 'end' }}>
          <div className="checkbox checkbox-info checkbox-circle">
            <input
              id="Liquid Marking"
              className="styled"
              type="checkbox"
              value="liquidMarking"
              checked={homepageStats === 'liquidMarking'}
              onChange={handleChangeHomepageStats}
            />
            <label htmlFor="Liquid Marking"></label>
          </div>
          <label
            htmlFor="Liquid Marking"
            style={{ fontFamily: 'Open Sans', fontSize: '12px', lineHeight: '15px' }}
          >
            Liquid Marking
          </label>
        </Grid>
        <Grid style={{ display: 'flex', alignItems: 'end' }}>
          <div className="checkbox checkbox-info checkbox-circle">
            <input
              id="eradication"
              className="styled"
              type="checkbox"
              value="eradication"
              checked={homepageStats === 'eradication'}
              onChange={handleChangeHomepageStats}
            />
            <label htmlFor="eradication"></label>
          </div>
          <label
            htmlFor="eradication"
            style={{ fontFamily: 'Open Sans', fontSize: '12px', lineHeight: '15px' }}
          >
            Eradication
          </label>
        </Grid>
      </Grid>
      <Grid container spacing={1} style={{ margin: '15px 0 0 0' }}>
        <Typography variant="body1" color="textSecondary">
          Click to edit
        </Typography>
      </Grid>
      <Grid container xs={12} style={{ marginTop: '7px' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragCancel}
          onDragCancel={handleDragCancel}
          onDragOver={handleDragEnd}
        >
          <SortableContext items={items} strategy={rectSortingStrategy}>
            <GridDND columns={5} classes={classes}>
              <ul className={classes.itemsList}>
                {items.map((elem, index) => (
                  <li key={index} style={{ margin: '0 8px 8px 0' }}>
                    <SortableItem
                      classes={classes}
                      key={elem._id}
                      id={elem._id}
                      elem={elem}
                      index={index}
                      editIndex={editIndex}
                      remove={remove}
                      typeOfLabel={'line'}
                      setItems={setItems}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setErrors({});
                        setEditIndex(index);
                        setWidth(elem.width);
                        setRatio(elem.ratio);
                        setHomepageStats(elem.homepageStats);
                        setId(elem._id);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </GridDND>
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <Item
                classes={classes}
                id={activeId}
                editIndex={editIndex}
                elem={items.find((el) => el._id === activeId)}
                typeOfLabel={'line'}
                isDragging
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </Grid>
    </Grid>
  );
};
export default withStyles(btnStyles)(LineSpecifications);
